import React from 'react';
import icoImg from '../assets/ico.png';
import icoTransparentImg from '../assets/ico_.png';
import { useLang } from '../i18n/useLang';

const expertiseCardConfig = [
  { stripeClass: 'bg-gradient-to-b from-nf-teal/30 to-transparent', icon: '🏥', page: 'services' },
  { stripeClass: 'bg-gradient-to-b from-nf-cyan/30 to-transparent', icon: '🤖', page: 'services' },
  { stripeClass: 'bg-gradient-to-b from-nf-p3/30 to-transparent', icon: '🧠', page: 'services' },
  { stripeClass: 'bg-gradient-to-b from-nf-amber/30 to-transparent', icon: '🗄️', page: 'services' },
  { stripeClass: 'bg-gradient-to-b from-nf-p/30 to-transparent', icon: '🔍', page: 'services' },
  { stripeClass: 'bg-gradient-to-b from-nf-rose/30 to-transparent', icon: '⚡', page: 'learn' },
  { stripeClass: 'bg-gradient-to-b from-nf-green/30 to-transparent', icon: '📊', page: 'blog' },
];

const processStepNums = ['01', '02', '03', '04'];

const pipelineConfig = [
  { icon: '💡', color: 'text-nf-amber', ring: 'border-nf-amber/25 bg-nf-amber/10', ani: 'animate-step-pop' },
  { icon: '⚙️', color: 'text-nf-p3', ring: 'border-nf-p3/25 bg-nf-p3/10', ani: 'animate-step-pop-d1' },
  { icon: '🔬', color: 'text-nf-teal', ring: 'border-nf-teal/25 bg-nf-teal/10', ani: 'animate-step-pop-d2' },
  { icon: '🤝', color: 'text-nf-cyan', ring: 'border-nf-cyan/25 bg-nf-cyan/10', ani: 'animate-step-pop-d3' },
  { icon: '🚀', color: 'text-nf-green', ring: 'border-nf-green/25 bg-nf-green/10', ani: 'animate-step-pop-d4' },
];

const progressConfig = [
  { pct: 85, grad: 'from-nf-teal to-nf-cyan', ani: 'animate-progress-fill' },
  { pct: 72, grad: 'from-nf-p to-nf-p3', ani: 'animate-progress-fill-d1' },
  { pct: 68, grad: 'from-nf-amber to-nf-rose', ani: 'animate-progress-fill-d2' },
  { pct: 92, grad: 'from-nf-green to-nf-teal', ani: 'animate-progress-fill-d3' },
];

export default function Home({ navigate }) {
  const { t } = useLang();
  const expertiseCards = expertiseCardConfig.map((cfg, i) => ({ ...cfg, ...t('home.expertiseCards')[i] }));
  const processSteps = processStepNums.map((n, i) => ({ n, ...t('home.processSteps')[i] }));
  const steps = pipelineConfig.map((cfg, i) => ({ ...cfg, ...t('home.steps')[i] }));
  const progressBars = progressConfig.map((cfg, i) => ({ ...cfg, lbl: t('home.progressBars')[i] }));
  return (
    <div>
      {/* Hero */}
      <div className="min-h-[90vh] flex items-center pt-[72px] sm:pt-[80px] pb-6 lg:pb-10 px-4 sm:px-8 lg:px-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 55% 65% at 20% 45%, rgba(109,40,217,.16) 0%, transparent 60%), radial-gradient(ellipse 35% 40% at 75% 70%, rgba(20,184,166,.07) 0%, transparent 55%)' }} />
        <div className="absolute inset-0 pointer-events-none opacity-[0.025]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

        <div className="max-w-[1100px] mx-auto w-full flex flex-col lg:flex-row items-center gap-6 lg:gap-0">
          {/* Left */}
          <div className="w-full lg:basis-[52%] relative z-[2]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-nf-bg3 border border-white/[0.08] text-xs text-nf-t2 mb-4 animate-fade-up">
              <div className="w-6 h-6 rounded-full bg-nf-bg2 flex items-center justify-center shrink-0">
                <img src={icoImg} alt="" style={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }} />
              </div>
              <span className="text-nf-text">{t('home.heroBadge')}</span>
            </div>
            <h1 className="font-heading font-black leading-none tracking-[-0.03em] mb-4 animate-fade-up-d1"
              style={{ fontSize: 'clamp(26px, 3.2vw, 46px)' }}>
              {t('home.h1')[0]}<br />
              <span className="text-nf-p3">{t('home.h1')[1]}</span><br />
              <span className="text-nf-teal">{t('home.h1')[2]}</span><br />
              <span className="block text-nf-acc font-bold mt-1 tracking-[-0.01em]"
                style={{ fontSize: 'clamp(13px, 1.4vw, 18px)' }}>
                {t('home.h1Sub')}
              </span>
            </h1>
            <p className="text-[15px] text-nf-t2 leading-[1.7] mb-5 animate-fade-up-d2">
              {t('home.heroBody')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-5 animate-fade-up-d3">
              <button className="btn-cta btn-lg w-full sm:w-auto" onClick={() => navigate('services')}>{t('home.btnExplore')}</button>
              <button className="btn-outline w-full sm:w-auto" onClick={() => navigate('learn')}>{t('home.btnLearn')}</button>
            </div>
            <div className="flex items-center gap-3 animate-fade-up-d4">
              <div className="flex">
                {['KN', 'AT', 'ZO', 'MS'].map((av) => (
                  <div key={av} className="w-8 h-8 rounded-full bg-nf-bg3 border-2 border-nf-bg flex items-center justify-center text-[10px] font-bold text-nf-t2 -ml-2 first:ml-0">{av}</div>
                ))}
                <div className="w-8 h-8 rounded-full bg-nf-p/20 border-2 border-nf-bg flex items-center justify-center text-[10px] font-bold text-nf-p3 -ml-2">+</div>
              </div>
              <span className="text-xs text-nf-t2"><strong className="text-nf-text">{t('home.teamCaption')[0]}</strong> {t('home.teamCaption')[1]}</span>
            </div>
          </div>

          {/* Right — Vision Animation */}
          <div className="flex flex-1 justify-center lg:justify-end relative z-[2] animate-fade-up-d4 w-full mt-4 lg:mt-0">
            <div className="relative w-full max-w-[460px] py-3">
              {/* Vision card */}
              <div className="w-full bg-nf-surface border border-[rgba(109,40,217,0.3)] rounded-[22px] overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,.6)]">
                {/* Player title bar */}
                <div className="flex items-center justify-between px-5 py-3 bg-nf-bg2 border-b border-white/[0.06]">
                  <div className="flex gap-[5px]">
                    <span className="w-[9px] h-[9px] rounded-full bg-[#ff5f57]" />
                    <span className="w-[9px] h-[9px] rounded-full bg-[#febc2e]" />
                    <span className="w-[9px] h-[9px] rounded-full bg-[#28c840]" />
                  </div>
                  <span className="flex items-center gap-1.5 text-[18px] ">
                    <img src={icoTransparentImg} alt="" className="w-4 h-4 object-contain opacity-80" />
                    {t('home.visionLabel')}
                  </span>
                  <span className="flex items-center gap-1.5 text-[10px] text-nf-green">
                    <span className="w-1.5 h-1.5 rounded-full bg-nf-green animate-nf-pulse inline-block" />
                    {t('home.live')}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  {/* Vision headline */}
                  <div className="text-center">
                    <div className="text-[10px] text-nf-t3 tracking-[0.12em] uppercase mb-2">{t('home.visionSecLabel')}</div>
                    <div className="font-heading text-[17px] font-black leading-tight">
                      {t('home.visionHeadline')[0]}{' '}<span className="text-nf-p3">{t('home.visionHeadline')[1]}</span>
                    </div>
                    <div className="text-[12px] text-nf-t2 mt-1.5">{t('home.visionSubtitle')}</div>
                  </div>
                  {/* Animated pipeline */}
                  <div className="flex items-start gap-0.5">
                    {steps.map((step, i) => (
                      <React.Fragment key={step.label}>
                        <div className="flex flex-col items-center flex-1 min-w-0">
                          <div className={`w-9 h-9 rounded-xl border ${step.ring} flex items-center justify-center text-base mb-1.5 ${step.ani}`}>
                            {step.icon}
                          </div>
                          <span className={`text-[9px] font-semibold text-center leading-tight ${step.color}`}>{step.label}</span>
                          {step.sub && <span className="text-[11px] font-bold text-nf-rose bg-nf-rose/10 border border-nf-rose/25 rounded px-1 py-px mt-0.5 leading-tight whitespace-nowrap">{step.sub}</span>}
                        </div>
                        {i < 4 && (
                          <div className="self-start mt-[18px] flex-none w-3 h-[2px] overflow-hidden rounded-full bg-white/[0.06]">
                            <div className="w-full h-full bg-gradient-to-r from-transparent via-nf-p3 to-transparent animate-slide-right" />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  {/* Animated progress bars */}
                  <div className="space-y-2.5">
                    {progressBars.map(({ lbl, pct, grad, ani }) => (
                      <div key={lbl} className="flex items-center gap-2">
                        <span className="text-[11px] text-nf-t2 w-[120px] shrink-0">{lbl}</span>
                        <div className="flex-1 h-[5px] bg-nf-bg2 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${grad} ${ani}`}
                            style={{ '--pct': `${pct}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-nf-t3 w-7 text-right">{pct}%</span>
                      </div>
                    ))}
                  </div>
                  {/* Status footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-nf-green animate-nf-pulse" />
                      <span className="text-[10px] text-nf-t2">{t('home.sprintStatus')}</span>
                    </div>
                    <span className="text-[10px] font-bold text-nf-teal">{t('home.sprintAvg')}</span>
                  </div>
                </div>
              </div>
              {/* Floating badge — top right */}
              <div className="absolute top-0 right-0 sm:right-[-8px] bg-nf-surface border border-nf-p/30 rounded-[14px] px-3 py-2.5 shadow-xl animate-float">
                <div className="text-[10px] text-nf-t3 mb-1">{t('home.badge1')[0]}</div>
                <div className="font-heading text-[20px] font-black text-nf-p3 leading-none">12</div>
                <div className="text-[9px] text-nf-t3 mt-0.5">{t('home.badge1')[1]}</div>
              </div>
              {/* Floating badge — bottom left */}
              <div className="absolute bottom-0 left-0 sm:left-[-8px] bg-nf-surface border border-nf-teal/30 rounded-[14px] px-3 py-2.5 shadow-xl animate-float-d1">
                <div className="text-[10px] text-nf-t3 mb-1">{t('home.badge2')[0]}</div>
                <div className="font-heading text-[20px] font-black text-nf-teal leading-none">14</div>
                <div className="text-[9px] text-nf-t3 mt-0.5">{t('home.badge2')[1]}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expertise */}
      <section className="py-16 lg:py-24 px-4 sm:px-8 lg:px-20 relative z-[1]">
        <div className="max-w-[1100px] mx-auto">
          <span className="sec-label">{t('home.expertiseLabel')}</span>
          <h2 className="sec-title">{t('home.expertiseTitle')}</h2>
          <p className="sec-sub">{t('home.expertiseSub')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {expertiseCards.map(c => (
              <div key={c.title}
                className="group relative bg-nf-bg3 border border-white/[0.06] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-[rgba(109,40,217,0.3)] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,.4)]"
                onClick={() => navigate(c.page)}>
                <div className={`absolute top-0 left-0 w-full h-16 ${c.stripeClass}`} />
                <div className="relative p-6">
                  <span className="text-[28px] mb-4 block">{c.icon}</span>
                  <h3 className="font-heading text-[16px] font-extrabold mb-2">{c.title}</h3>
                  <p className="text-[13px] text-nf-t2 leading-[1.65] mb-4">{c.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.chips.map(ch => <span key={ch} className="chip">{ch}</span>)}
                  </div>
                  <div className="absolute inset-0 bg-nf-p/[0.04] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-2xl" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-nf-bg2 relative z-[1]">
        <div className="max-w-[1100px] mx-auto">
          <span className="sec-label">{t('home.processLabel')}</span>
          <h2 className="sec-title">{t('home.processTitle')}</h2>
          <p className="sec-sub">{t('home.processSub')}</p>
          <div className="process-timeline grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative mt-10 lg:mt-14">
            {processSteps.map(s => (
              <div key={s.n} className="relative">
                <div className="w-14 h-14 rounded-full bg-nf-bg3 border border-[rgba(109,40,217,0.3)] flex items-center justify-center font-heading font-black text-nf-p3 text-lg mb-5 relative z-[1]">{s.n}</div>
                <h4 className="font-heading text-[17px] font-extrabold mb-2">{s.title}</h4>
                <p className="text-sm text-nf-t2 leading-[1.65]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="py-16 lg:py-[120px] px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden bg-nf-bg">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_50%_50%,rgba(109,40,217,0.14),transparent_70%)] pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto">
          <h2 className="font-heading font-black leading-[1.05] tracking-[-0.03em] max-w-[740px] mx-auto mb-5"
            style={{ fontSize: 'clamp(30px, 4.5vw, 58px)' }}>
            {t('home.ctaHeading')[0]}<span className="text-nf-p3">{t('home.ctaHeading')[1]}</span>
          </h2>
          <p className="text-[17px] text-nf-t2 max-w-[500px] mx-auto mb-11">
            {t('home.ctaBody')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
            <button className="btn-cta btn-lg w-full sm:w-auto" onClick={() => navigate('learn')}>{t('home.ctaBtnPrimary')}</button>
            <button className="btn-outline w-full sm:w-auto" onClick={() => navigate('blog')}>{t('home.ctaBtnSecondary')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
